import React from 'react';
import { Calendar } from 'lucide-react';
import { Card, CardContent, Box, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for month pills
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow: theme.shadows[2],
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const MonthContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const MonthPill = styled(Paper)(({ theme, color }) => ({
  padding: '12px 20px',
  borderRadius: '8px',
  boxShadow: theme.shadows[1],
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
  ...(color === 'blue' && {
    backgroundColor: '#dbeafe',
    border: '2px solid #60a5fa',
  }),
  ...(color === 'amber' && {
    backgroundColor: '#fed7aa',
    border: '2px solid #fbbf24',
  }),
  ...(color === 'emerald' && {
    backgroundColor: '#d1fae5',
    border: '2px solid #34d399',
  }),
}));

const MonthLabel = styled(Typography)(({ theme, color }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  ...(color === 'blue' && {
    color: '#1e40af',
  }),
  ...(color === 'amber' && {
    color: '#b45309',
  }),
  ...(color === 'emerald' && {
    color: '#047857',
  }),
}));

const MonthValue = styled(Typography)(({ theme, color }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  ...(color === 'blue' && {
    color: '#1e3a8a',
  }),
  ...(color === 'amber' && {
    color: '#92400e',
  }),
  ...(color === 'emerald' && {
    color: '#065f46',
  }),
}));

const MonthPills = ({ months }) => {
  const getMonthLabel = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <StyledCard data-testid="month-pills">
      <CardContent sx={{ p: 4 }}>
        <HeaderBox>
          <Calendar size={20} style={{ color: '#4f46e5' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            Month Overview
          </Typography>
        </HeaderBox>
        
        <MonthContainer>
          {months.CM2 && (
            <MonthPill color="blue" data-testid="month-cm2">
              <MonthLabel color="blue">CM-2</MonthLabel>
              <MonthValue color="blue">{getMonthLabel(months.CM2)}</MonthValue>
            </MonthPill>
          )}
          
          {months.CM1 && (
            <MonthPill color="amber" data-testid="month-cm1">
              <MonthLabel color="amber">CM-1</MonthLabel>
              <MonthValue color="amber">{getMonthLabel(months.CM1)}</MonthValue>
            </MonthPill>
          )}
          
          {months.CM && (
            <MonthPill color="emerald" data-testid="month-cm">
              <MonthLabel color="emerald">CM</MonthLabel>
              <MonthValue color="emerald">{getMonthLabel(months.CM)}</MonthValue>
            </MonthPill>
          )}
        </MonthContainer>
      </CardContent>
    </StyledCard>
  );
};

export default MonthPills;